import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  EMPTY,
  filter,
  fromEvent,
  merge,
  Observable,
  Subscription,
  switchMap,
  take,
  tap,
  throttleTime
} from 'rxjs';

import { AuthFacade } from '~console/app/~store/facades/auth.facade';
import { InactivityCheckDialogComponent } from '~console/app/~shared/components/modals/inactivity-check-dialog/inactivity-check-dialog.component';
import {
  DEFAULT_INACTIVITY_CHECK_CONFIG,
  InactivityCheckConfig,
  InactivityCheckDialogResult,
  InactivityCheckEnvironmentOverrides
} from '~console/app/~shared/models/inactivity-check.models';
import { DialogService } from '~console/app/~shared/services/dialog.service';
import { ConstantsService } from '~core/services/constants.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityCheckService {
  private readonly _document = inject(DOCUMENT);
  private readonly _ngZone = inject(NgZone);
  private readonly _dialogService = inject(DialogService);
  private readonly _authFacade = inject(AuthFacade);
  private readonly _constantsService = inject(ConstantsService);

  private _activitySubscription?: Subscription;
  private _inactivityTimeoutId?: ReturnType<typeof setTimeout>;
  private _warningDialogRef?: MatDialogRef<
    InactivityCheckDialogComponent,
    InactivityCheckDialogResult
  >;

  private _warningDialogSubscription?: Subscription;

  private _isMonitoring = false;

  private _isWarningVisible = false;

  startMonitoring(): void {
    if (this._isMonitoring || !this._document.defaultView) {
      return;
    }

    this._isMonitoring = true;

    this._ngZone.runOutsideAngular(() => {
      const config = this._getConfig();
      this._activitySubscription = this._createActivityStream(config)
        .pipe(
          tap(() => {
            if (!this._isWarningVisible) {
              this._scheduleInactivityTimeout(config);
            }
          })
        )
        .subscribe();

      this._scheduleInactivityTimeout(config);
    });
  }

  stopMonitoring(): void {
    if (!this._isMonitoring) {
      return;
    }

    this._isMonitoring = false;

    this._activitySubscription?.unsubscribe();
    this._activitySubscription = undefined;

    this._warningDialogSubscription?.unsubscribe();
    this._warningDialogSubscription = undefined;

    if (this._inactivityTimeoutId) {
      clearTimeout(this._inactivityTimeoutId);
      this._inactivityTimeoutId = undefined;
    }

    this._isWarningVisible = false;
    this._warningDialogRef?.close();
    this._warningDialogRef = undefined;
  }

  private _createActivityStream(
    config: InactivityCheckConfig
  ): Observable<Event> {
    const window = this._document.defaultView;

    if (!window) {
      return EMPTY;
    }

    return merge(
      fromEvent(this._document, 'mousemove'),
      fromEvent(this._document, 'keydown'),
      fromEvent(this._document, 'click'),
      fromEvent(this._document, 'scroll'),
      fromEvent(this._document, 'pointerdown'),
      fromEvent(this._document, 'touchstart'),
      fromEvent(this._document, 'touchmove'),
      fromEvent(window, 'focus'),
      fromEvent(this._document, 'visibilitychange').pipe(
        filter(() => !this._document.hidden)
      )
    ).pipe(throttleTime(config.activityThrottleMs));
  }

  private _scheduleInactivityTimeout(config: InactivityCheckConfig): void {
    if (this._inactivityTimeoutId) {
      clearTimeout(this._inactivityTimeoutId);
    }

    this._inactivityTimeoutId = setTimeout(() => {
      this._openWarningDialog(config);
    }, config.inactivityDurationMs);
  }

  private _openWarningDialog(config: InactivityCheckConfig): void {
    if (!this._isMonitoring || this._isWarningVisible) {
      return;
    }

    this._isWarningVisible = true;

    this._ngZone.run(() => {
      this._warningDialogRef = this._dialogService.open({
        component: InactivityCheckDialogComponent,
        config: {
          panelClass: ['mat-dialog-custom'],
          disableClose: true,
          data: {
            warningDurationSeconds: config.warningDurationSeconds
          }
        }
      });

      this._warningDialogSubscription = this._warningDialogRef
        .afterClosed()
        .pipe(
          take(1),
          tap(result => {
            this._isWarningVisible = false;
            this._warningDialogRef = undefined;

            if (!this._isMonitoring) {
              return;
            }

            if (result === 'stay') {
              this._scheduleInactivityTimeout(config);
            }
          }),
          filter(result => this._isMonitoring && result !== 'stay'),
          switchMap(() => this._authFacade.logout().pipe(take(1)))
        )
        .subscribe();
    });
  }

  private _getConfig(): InactivityCheckConfig {
    const environment =
      this._constantsService.environment as InactivityCheckEnvironmentOverrides;

    const inactivityDurationMs = this._toPositiveNumber(
      environment.inactivityCheckTimeoutMs,
      DEFAULT_INACTIVITY_CHECK_CONFIG.inactivityDurationMs
    );

    const warningDurationSeconds = this._toPositiveNumber(
      environment.inactivityCheckWarningSeconds,
      DEFAULT_INACTIVITY_CHECK_CONFIG.warningDurationSeconds
    );

    const activityThrottleMs = this._toPositiveNumber(
      environment.inactivityCheckActivityThrottleMs,
      DEFAULT_INACTIVITY_CHECK_CONFIG.activityThrottleMs
    );

    return {
      inactivityDurationMs,
      warningDurationSeconds,
      activityThrottleMs
    };
  }

  private _toPositiveNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && value > 0 ? value : fallback;
  }
}
